import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TableSortLabel, TablePagination, Chip,
  CircularProgress, Alert, Divider, IconButton, Tooltip, Button,
  ButtonGroup, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SfColumnHeader from '../SfColumnHeader';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { mkConfig, generateCsv, download as csvDownload } from 'export-to-csv';
import { exportToPdf } from '../exportPdf';
import { OPPORTUNITY_COLUMNS } from './drillConfigs';
import { workshopNames } from '../WorkshopLink';
import { sfOpportunityUrl } from '../../utils/externalLinks';
import { formatCurrency } from '../../utils/formatCurrency';
import { stageChipColor } from '../../utils/stageRules';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const fmtCurrency = (v) => formatCurrency(v);

const stageColor = stageChipColor;

// Legacy columns kept for backward compat when no panelConfig is provided
const COLUMNS = OPPORTUNITY_COLUMNS;

const FILTER_KEYS = ['stage', 'productFamily', 'quarter', 'workshop', 'account',
                     'amountMin', 'amountMax', 'geography', 'region', 'minWorkshops', 'maxWorkshops'];

function buildLegacyParams({ startDate, endDate, windowMonths, postWorkshopOnly, filters, page, pageSize, sortBy, sortOrder }) {
  const p = new URLSearchParams();
  if (startDate) p.set('startDate', startDate);
  if (endDate)   p.set('endDate', endDate);
  p.set('windowMonths', String(windowMonths ?? 18));
  p.set('postWorkshopOnly', String(postWorkshopOnly ?? false));
  if (page !== undefined)     p.set('page', String(page + 1));
  if (pageSize !== undefined) p.set('pageSize', String(pageSize));
  FILTER_KEYS.forEach(k => {
    const v = filters?.[k];
    if (v != null && v !== '') p.set(k, String(v));
  });
  return p;
}

function toExportRows(rows, columns) {
  return rows.map(r => {
    const out = {};
    columns.forEach(col => {
      out[col.label] = (col.exportFormat || col.format) ? (col.exportFormat || col.format)(r[col.id]) : (r[col.id] ?? '');
    });
    return out;
  });
}

/**
 * Inline drill-down panel. Supports three panel types via panelConfig prop:
 *   panelType 'opportunities' — opp rows with Salesforce links + stage chips (default)
 *   panelType 'participants'  — one row per SE with workshop count + pipeline columns
 *   panelType 'workshops'     — one row per workshop with deduped pipeline + account count
 *
 * Props:
 *   title, filters, startDate, endDate, windowMonths, postWorkshopOnly
 *   panelConfig  — { endpoint, buildParams, columns, totalKey, idKey, panelType }
 *                  when absent falls back to legacy /exec/opportunities behaviour
 *   onClose      — optional; close button hidden when omitted (DrillPage)
 *   hideNewTab   — hides "open in new tab" button (avoids recursion in DrillPage)
 *   note         — optional context-specific warning rendered below the disclaimer
 */
export default function DrillPanel({
  title = 'Opportunities',
  filters = {},
  startDate,
  endDate,
  windowMonths = 18,
  postWorkshopOnly = false,
  panelConfig,
  onClose,
  hideNewTab = false,
  note,
}) {
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalAmount, setTotalAmt]= useState(0);
  const [amountNA, setAmountNA]     = useState(false); // API says dollars do not apply (e.g. TEC Program tag)
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRPP]     = useState(25);
  const [orderBy, setOrderBy]     = useState(null);
  const [order, setOrder]         = useState('desc');
  const [exporting, setExporting] = useState(false);
  const generationRef = useRef(null); // influence lists: the snapshot generation page 1 was served from (later pages send it back)

  const activeColumns = panelConfig?.columns ?? COLUMNS;
  const panelType     = panelConfig?.panelType ?? 'opportunities';
  const totalKey      = panelConfig?.totalKey ?? 'oppamt';
  const idKey         = panelConfig?.idKey ?? 'oppid';

  // Default sort: first numeric right-aligned column, or second column
  const defaultSort = useMemo(() => {
    const numeric = activeColumns.find(c => c.align === 'right');
    return numeric?.id ?? activeColumns[1]?.id ?? activeColumns[0]?.id ?? 'oppamt';
  }, [activeColumns]);

  const effectiveOrderBy = orderBy ?? defaultSort;

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const scopeKey = panelConfig?.scopeKey ?? ''; // a config that carries its own scope (influence lists) refetches when the scope changes

  useEffect(() => { setPage(0); }, [filtersKey, scopeKey, startDate, endDate]);

  // Reset sort when panel type changes
  useEffect(() => { setOrderBy(null); }, [panelType]);

  const buildUrl = (pg, ps) => {
    if (panelConfig) {
      const base = {
        startDate, endDate, windowMonths, postWorkshopOnly,
        page: pg, pageSize: ps,
        sortBy: effectiveOrderBy, sortOrder: order,
        generation: pg > 0 ? generationRef.current : null,
      };
      const p = panelConfig.buildParams(base, filters);
      return `${API_BASE_URL}${panelConfig.endpoint}?${p}`;
    }
    const p = buildLegacyParams({
      startDate, endDate, windowMonths, postWorkshopOnly,
      filters, page: pg, pageSize: ps,
    });
    return `${API_BASE_URL}/exec/opportunities?${p}`;
  };

  useEffect(() => {
    let live = true; // a slower, older request (another sort or page) must not overwrite the rows, total and generation of the newer one
    setLoading(true);
    setError(null);
    fetch(buildUrl(page, rowsPerPage), { credentials: 'include' })
      .then(r => {
        if (!live) return null;
        if (r.status === 409 && panelConfig?.normalize) {
          // the nightly snapshot changed while paging: the later pages no longer line up, so start again at page 1
          generationRef.current = null;
          if (page !== 0) { setPage(0); return null; }
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(raw => {
        if (raw === null || !live) return;
        const d = panelConfig?.normalize ? panelConfig.normalize(raw) : raw;
        if (panelConfig?.normalize && page === 0) generationRef.current = raw.generation ?? null;
        const dataRows = d.rows || (Array.isArray(d) ? d : []);
        setRows(dataRows);
        setTotal(d.total ?? dataRows.length);
        setAmountNA(d.amount_applicable === 0 || panelConfig?.amountNA === true);
        // total_amount in opp endpoints; sum totalKey manually for non-opp panels
        if (d.total_amount !== undefined) {
          setTotalAmt(d.total_amount);
        } else {
          setTotalAmt(dataRows.reduce((s, r) => s + (parseFloat(r[totalKey]) || 0), 0));
        }
        setLoading(false);
      })
      .catch(e => { if (live) { setError(e.message); setLoading(false); } });
    return () => { live = false; };
  }, [startDate, endDate, windowMonths, postWorkshopOnly, filtersKey, scopeKey, page, rowsPerPage, effectiveOrderBy, order]); // eslint-disable-line react-hooks/exhaustive-deps

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const sortVal = panelConfig?.columns?.find(c => c.id === effectiveOrderBy)?.sortValue;
    const val = (r) => (sortVal ? sortVal(r[effectiveOrderBy]) : effectiveOrderBy === 'workshops' ? workshopNames(r.workshops) : r[effectiveOrderBy]) ?? '';
    const av = val(a);
    const bv = val(b);
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return order === 'asc' ? cmp : -cmp;
  }), [rows, effectiveOrderBy, order, panelConfig]);

  const pageTotal = useMemo(
    () => sorted.reduce((s, r) => s + (parseFloat(r[totalKey]) || 0), 0),
    [sorted, totalKey]
  );

  const handleSort = (col) => {
    if (effectiveOrderBy === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('desc'); }
  };

  const sfLink = sfOpportunityUrl;

  const openInNewTab = () => {
    const p = panelConfig
      ? panelConfig.buildParams({ startDate, endDate, windowMonths, postWorkshopOnly, sortBy: effectiveOrderBy, sortOrder: order }, filters)
      : buildLegacyParams({ startDate, endDate, windowMonths, postWorkshopOnly, filters });
    p.set('title', title);
    p.set('panelType', panelType);
    window.open(`/exec/drill?${p}`, '_blank');
  };

  const fetchAllForExport = async () => {
    const step = panelConfig?.maxPageSize; // an endpoint with a page cap is read page by page (up to 5000 rows)
    if (step) {
      const out = [];
      generationRef.current = null; // the export pins the generation of its own first page
      for (let pg = 0; pg < 25 && (pg === 0 || out.length < total); pg += 1) {
        const r = await fetch(buildUrl(pg, step), { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const raw = await r.json();
        if (pg === 0 && panelConfig.normalize) generationRef.current = raw.generation ?? null;
        const d = panelConfig.normalize ? panelConfig.normalize(raw) : raw;
        out.push(...(d.rows || []));
        if (!d.rows?.length) break;
      }
      return out;
    }
    const url = buildUrl(0, 500);
    const r = await fetch(url, { credentials: 'include' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    return d.rows || (Array.isArray(d) ? d : []);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const all  = await fetchAllForExport();
      const data = toExportRows(all, activeColumns);
      const slug = title.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
      const cfg  = mkConfig({ filename: slug, useKeysAsHeaders: true });
      csvDownload(cfg)(generateCsv(cfg)(data));
    } catch (e) { console.error('CSV export failed', e); }
    finally { setExporting(false); }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const all  = await fetchAllForExport();
      const data = toExportRows(all, activeColumns);
      const XLSX = await import('xlsx');
      const ws   = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = Object.keys(data[0] || {}).map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] ?? '').length)) + 2,
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
      XLSX.writeFile(wb, `${title.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase()}.xlsx`);
    } catch (e) { console.error('Excel export failed', e); }
    finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const all     = await fetchAllForExport();
      const pdfCols = activeColumns.map(c => ({ header: c.label, key: c.id }));
      await exportToPdf(all.map(r => (Array.isArray(r.workshops) ? { ...r, workshops: workshopNames(r.workshops) } : r)), { title, columns: pdfCols });
    } catch (e) { console.error('PDF export failed', e); }
    finally { setExporting(false); }
  };

  const isOppPanel = panelType === 'opportunities';
  const coverageText = panelConfig?.coverageLine && !loading && !error && rows.length > 0 ? panelConfig.coverageLine(rows) : null;

  const totalsLabel = useMemo(() => {
    if (amountNA) return '—';
    if (!isOppPanel) return fmtCurrency(totalAmount);
    if (title.includes('Avg Deal Size')) {
      return `${fmtCurrency(totalAmount)} (${total.toLocaleString()} deals)`;
    }
    return fmtCurrency(totalAmount);
  }, [isOppPanel, totalAmount, total, title, amountNA]);

  const rowCountLabel = (n) => {
    if (panelType === 'participants') return n === 1 ? 'participant' : 'participants';
    if (panelType === 'workshops')    return n === 1 ? 'workshop' : 'workshops';
    return n === 1 ? 'opportunity' : 'opportunities';
  };

  return (
    <Box sx={{ mt: 2, borderLeft: '3px solid rgb(218,41,28)', borderRadius: 1, backgroundColor: '#fafafa', p: 1.5 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1, fontSize: '0.85rem' }}>
          {title}
          {!loading && (
            <Box component="span" sx={{ ml: 1.5, color: 'text.secondary', fontWeight: 400, fontSize: '0.8rem' }}>
              {total.toLocaleString()} {rowCountLabel(total)}
            </Box>
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {exporting && <CircularProgress size={14} />}
          <ButtonGroup size="small" variant="outlined" disabled={exporting || loading || !!error}>
            <Tooltip title="Export to CSV"><Button onClick={handleExportCsv} sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}>CSV</Button></Tooltip>
            <Tooltip title="Export to Excel (.xlsx)"><Button onClick={handleExportExcel} sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}>Excel</Button></Tooltip>
            <Tooltip title="Export to PDF"><Button onClick={handleExportPdf} sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}>PDF</Button></Tooltip>
          </ButtonGroup>
          {!hideNewTab && !panelConfig?.noNewTab && (
            <Tooltip title="Open in new tab for side-by-side comparison">
              <IconButton size="small" onClick={openInNewTab} disabled={loading || !!error}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Collapse">
              <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Totals bar ── */}
      {!loading && !error && total > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, px: 1, py: 0.5,
          backgroundColor: '#e8eaf6', borderRadius: 1, mb: 0.75, flexWrap: 'wrap',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Total ({total.toLocaleString()} rows):&nbsp;
            <Box component="span" sx={{ color: '#1976d2' }}>{totalsLabel}</Box>
          </Typography>
          {rows.length < total && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              · This page: {fmtCurrency(pageTotal)}
            </Typography>
          )}
        </Box>
      )}

      {/* ── Disclaimer (opp panels only) ── */}
      {isOppPanel && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.68rem', mb: 0.5 }}>
          {panelConfig?.disclaimer
            ? panelConfig.disclaimer
            : panelConfig
            ? '✓ Deduplicated: one row per Salesforce deal — totals match KPI figures. Small differences (±5%) are normal when a deal is linked to multiple workshops with mixed stage history.'
            : '⚠ Row totals may exceed KPI figures: one Salesforce deal can appear once per workshop check-in it was linked to. KPI cards count each deal once.'}
        </Typography>
      )}

      {/* ── Plan 2217: a list whose blank cells could be misread as "no influence" says how many rows have a value ── */}
      {coverageText && (
        <Typography variant="caption" data-testid={panelConfig.coverageTestId || 'drill-coverage'} sx={{ display: 'block', color: 'text.secondary', fontSize: '0.68rem', mb: 0.5 }}>
          {coverageText}
        </Typography>
      )}

      {/* ── Optional context note ── */}
      {note && (
        <Alert severity="warning" sx={{ py: 0.25, px: 1, mb: 0.75, fontSize: '0.72rem', '& .MuiAlert-message': { py: 0.5 } }}>
          {note}
        </Alert>
      )}

      <Divider sx={{ mb: 1 }} />

      {/* ── Body ── */}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>}
      {error && <Alert severity="error">Failed to load: {error}</Alert>}
      {!loading && !error && rows.length === 0 && (
        <Typography variant="body2" sx={{ py: 1.5, color: 'text.secondary' }}>
          No {rowCountLabel(0)} match the selected filters.
        </Typography>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <TableContainer sx={{ maxHeight: 420, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  {isOppPanel && <TableCell sx={{ width: 32, p: 0.5, backgroundColor: '#f5f5f5', fontWeight: 700, fontSize: '0.72rem' }}><SfColumnHeader nameOpensDeal={activeColumns.some((c) => c.opensDeal)} /></TableCell>}
                  {activeColumns.map(col => (
                    <TableCell
                      key={col.id}
                      align={col.align || 'left'}
                      sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap',
                            width: col.width, backgroundColor: '#f5f5f5', py: 0.75 }}
                    >
                      {(() => {
                        const label = col.sortable === false ? col.label : (
                          <TableSortLabel
                            active={effectiveOrderBy === col.id}
                            direction={effectiveOrderBy === col.id ? order : 'asc'}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        );
                        return col.tooltip ? <Tooltip title={col.tooltip} arrow><span>{label}</span></Tooltip> : label;
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row, idx) => {
                  const botPct = row.bot_pct != null ? parseFloat(row.bot_pct) : null;
                  const botCount = row.bot_count ?? null;
                  const totalCount = row.participant_count ?? null;
                  const botBadgeColor = botPct != null && botPct >= 0.5 ? '#d32f2f' : '#f57c00';
                  const showBotBadge = panelType === 'workshops' && botPct != null && botPct >= 0.2;
                  return (
                  <TableRow
                    key={row[idKey] || idx}
                    hover
                    onClick={isOppPanel && row.oppid ? () => window.open(sfLink(row.oppid), '_blank', 'noopener') : undefined}
                    sx={{ cursor: isOppPanel && row.oppid ? 'pointer' : 'default' }}
                  >
                    {isOppPanel && (
                      <TableCell sx={{ p: 0.5 }} onClick={e => e.stopPropagation()}>
                        {row.oppid && (
                          <Tooltip title="Open in Salesforce">
                            <IconButton size="small" component={Link} href={sfLink(row.oppid)} target="_blank" rel="noopener">
                              <OpenInNewIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    {activeColumns.map(col => (
                      <TableCell
                        key={col.id}
                        align={col.align || 'left'}
                        sx={{ fontSize: '0.72rem', py: 0.5,
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: col.noWrap === false ? 'normal' : 'nowrap',
                              maxWidth: col.width }}
                      >
                        {col.renderCell
                          ? col.renderCell(row[col.id], row, { startDate, endDate })
                          : col.chipType === 'stage'
                            ? <Chip label={row[col.id] || '—'} size="small" title={col.detailKey ? row[col.detailKey] || undefined : undefined}
                                    color={stageColor(row[col.id])} variant="outlined"
                                    sx={{ fontSize: '0.62rem', height: 18 }} />
                            : isOppPanel && col.id === 'stage_name'
                              ? <Chip label={row.stage_name || '—'} size="small"
                                      color={stageColor(row.stage_name)} variant="outlined"
                                      sx={{ fontSize: '0.62rem', height: 18 }} />
                              : col.format ? col.format(row[col.id]) : (row[col.id] ?? '—')
                        }
                        {showBotBadge && col === activeColumns[activeColumns.length - 1] && (
                          <Tooltip
                            title={botCount != null && totalCount != null
                              ? `${botCount} of ${totalCount} participants in this instance flagged as bots`
                              : `${Math.round(botPct * 100)}% of participants flagged as bots`}
                            placement="top"
                            arrow
                          >
                            <Chip
                              label={`${Math.round(botPct * 100)}% bots`}
                              size="small"
                              sx={{
                                ml: 0.5, bgcolor: botBadgeColor, color: '#fff',
                                fontSize: '0.62rem', height: 18,
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {!panelConfig?.unpaged && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRPP(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': { minHeight: 36 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
            }}
          />
          )}
        </>
      )}
    </Box>
  );
}
